import { Head, Link, useForm } from '@inertiajs/react'
import { HomeMark } from '../../shared/HomeMark.jsx'
import { withSiteFooter } from '../../shared/SiteFooter.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'

// `purchase` is the plan a "Buy" button on /pricing chose — `{ plan, label,
// interval, price, businesses }` — or null for "Start free". With one, this page
// signs up to buy that plan, and says nothing about a trial: the trial and the
// purchase are two separate ways in, decided 2026-10-27. Its plan and interval
// travel with the form; empty for a trial, which the server reads as no purchase.
export default function SignUp({ errors = {}, trial_days = null, purchase = null }) {
  const form = useForm({
    email: '',
    password: '',
    plan: purchase ? purchase.plan : '',
    interval: purchase ? purchase.interval : '',
  })
  const signInHref = purchase
    ? `/session/new?plan=${purchase.plan}&interval=${purchase.interval}`
    : '/session/new'

  function submit(e) {
    e.preventDefault()
    form.post('/sign-up')
  }

  return (
    <>
      <Head title={purchase ? `Buy ${purchase.label}` : 'Start free'} />
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-8 px-6 py-16">
        <HomeMark />
        <div className="flex flex-col gap-2">
          <h1 className="page-title">{purchase ? `Buy ${purchase.label}` : 'Start measuring'}</h1>
          {/*
            Two lines, in this order: what the product is, then what trying it
            costs. Somebody arriving cold — from a shared link or the grader's
            email — needs orienting before they can weigh the terms.

            **The second line is the only statement of the trial on the page
            where the decision is made.** `/pricing` says it too, and a visitor
            who came straight here has not read it. "No credit card" is the fact that
            lowers the barrier, so it belongs beside the button rather than one
            navigation away.

            It is also the only consumer of `trial_days`, which
            `RegistrationsController` passes as `Account.trial_days` precisely so
            the number is never written into prose. Drop this line and the prop
            goes dead, and the next person to want the sentence types "14".

            Muted and smaller, matching `Auth/Verify`: the pitch leads and the
            terms support it, rather than two sentences competing at one weight.
          */}
          {purchase ? (
            <>
              <p className="text-[15px] text-[color:var(--text-body)]">
                {purchase.label} is {purchase.price}{purchase.interval === 'year' ? ', billed up front' : ''}, for{' '}
                {purchase.businesses === 1 ? 'one business' : `${purchase.businesses} businesses`}.
              </p>
              <p className="text-[13.5px] text-[color:var(--text-muted)]">
                Create your account, confirm your email, then pay on Stripe.
              </p>
            </>
          ) : (
            <>
              <p className="text-[15px] text-[color:var(--text-body)]">
                See how AI assistants answer when someone in your city is looking for a business like yours.
              </p>
              <p className="text-[13.5px] text-[color:var(--text-muted)]">
                No credit card.{trial_days ? ` ${trial_days} days of real measurement, then decide.` : ' Real measurement first, then decide.'}
              </p>
            </>
          )}
        </div>

        {/* Re-applied on integration. `RegistrationsController` writes its rate limit to
            `errors.base`, and no field on this page reads that key — so the
            failure returned a clean redirect and the page said **nothing at
            all**. An error nobody prints is the quiet half of the one-flat-bag
            problem in CLAUDE.md, and on sign-in it landed on the most-used form
            in the product. Verified in a browser 2026-09-24: the message is in
            the props and the page never renders it. */}
        {errors.base && (
          <Alert tone="signal" icon="alert-circle">
            {errors.base}
          </Alert>
        )}

        <form onSubmit={submit} className="flex flex-col gap-5">
          <Field label="Email" htmlFor="email" required error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@agency.com"
              value={form.data.email}
              invalid={Boolean(errors.email)}
              onChange={(e) => form.setData('email', e.target.value)}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            required
            hint="At least 8 characters, including a number and a symbol."
            error={errors.password}
          >
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={form.data.password}
              invalid={Boolean(errors.password)}
              onChange={(e) => form.setData('password', e.target.value)}
            />
          </Field>

          <Button variant="primary" size="lg" block as="button" loading={form.processing}>
            Create account
          </Button>

          <p className="text-[13.5px] text-[color:var(--text-muted)]">
            {purchase
              ? 'After you pay, you set up the business: its name, industry and city.'
              : 'The business, its industry and its city come next, where each one can be explained and corrected.'}
          </p>
        </form>

        <div className="flex flex-col gap-2">
          <p className="text-[13.5px] text-[color:var(--text-muted)]">
            Already have an account?{' '}
            <Link href={signInHref} className="underline underline-offset-4">
              Sign in
            </Link>
          </p>
          {/* The trial, offered once and quietly on the buying page — every other
              public page offers it in its header, and this one has no header. */}
          {purchase && (
            <p className="text-[13.5px] text-[color:var(--text-muted)]">
              Rather try it first?{' '}
              <Link href="/sign-up" className="underline underline-offset-4">
                Start a {trial_days ? `${trial_days}-day ` : ''}free trial
              </Link>
            </p>
          )}
        </div>
      </main>
    </>
  )
}

SignUp.layout = withSiteFooter
